import { browserHistory, RedirectFunction, RouterState } from "react-router";
import { syncHistoryWithStore as syncHistoryWithStoreMOBX } from "mobx-react-router";
import { rootStore } from "#stores/index";
import { clearQueryClient } from "#structure/providers/react-query";
import { PlaceWizardStatus } from "#resources/api/enterprise-generated";
import { isFeatureFlagEnabled } from "#resources/hooks/integrations/feature-flags/is-feature-flag-enabled";
import { FeatureFlagEnum } from "#resources/hooks/use-has-feature-flag-enabled";
import {
	isEventRouteAllowedWhenExpired,
	isPlaceRouteAllowedWhenExpired,
} from "./expired-place-routes";

export const history = syncHistoryWithStoreMOBX(browserHistory, rootStore.routerStore);

async function setPlaceAndEventContext(nextState: RouterState): Promise<void> {
	if (nextState.params.placeId) {
		await rootStore.placeStore.setCurrentPlace(nextState.params.placeId);
	} else {
		await rootStore.placeStore.unsetCurrentPlace();
	}

	if (nextState.params.eventId) {
		await rootStore.eventStore.setCurrentEvent(nextState.params.eventId);
	} else {
		await rootStore.eventStore.unsetCurrentEvent();
	}
}

function checkPlaceExpired(nextState: RouterState): boolean {
	const isPlaceExpired = rootStore.placeStore.place?.isExpired ?? false;

	if (!isPlaceExpired || !nextState.params.placeId) {
		return false;
	}

	const { placeId, eventId } = nextState.params;
	const pathname = nextState.location.pathname;

	const isPlaceRoute = !eventId;
	const isEventRoute = !!eventId;

	if (isPlaceRoute) {
		const isAllowed = isPlaceRouteAllowedWhenExpired(placeId, pathname);

		if (!isAllowed) {
			rootStore.routerStore?.replace(`/place/${placeId}/business-intelligence`);
			return true;
		}
	}

	if (isEventRoute) {
		const isAllowed = isEventRouteAllowedWhenExpired(placeId, eventId, pathname);

		if (!isAllowed) {
			rootStore.routerStore?.replace(`/place/${placeId}/event/${eventId}`);
			return true;
		}
	}

	return false;
}

/**
 * Verifica se há restrições de vendor e redireciona para a rota correta
 * @param nextState - Estado da rota atual
 * @returns true se houver restrições de vendor e a rota foi redirecionada, false caso contrário
 */
function checkVendorRestrictionRedirect(nextState: RouterState): boolean {
	const { placeId, eventId } = nextState.params;

	if (!eventId || !placeId) {
		return false;
	}

	const hasVendorRestrictions = !!rootStore.authStore.currentEmployee?.vendors?.length;

	if (!hasVendorRestrictions) {
		return false;
	}

	return false;
}

export async function requireAuth(
	nextState: RouterState,
	_replace: RedirectFunction,
	callback: () => void | undefined,
) {
	if (!rootStore.authStore.currentEmployee) {
		const currentRoute = history.getCurrentLocation();
		if (currentRoute.pathname !== "/login") {
			sessionStorage.setItem("redirectAfterLogin", currentRoute.pathname);
		}
		clearQueryClient();
		rootStore.routerStore?.push("/login");
	} else {
		await rootStore.organizationStore.fetchCurrentOrganization();
		await setPlaceAndEventContext(nextState);
		checkPlaceExpired(nextState);
		checkVendorRestrictionRedirect(nextState);
	}
	callback();
}

export function requireNotAuth() {
	if (rootStore.authStore.currentEmployee) {
		rootStore.routerStore.push("/");
	}
}

export function requireValidProposalToken(
	nextState: RouterState,
	replace: RedirectFunction,
	callback: () => void | undefined,
) {
	const tokenId = nextState.params.tokenId;
	if (!tokenId || tokenId.trim().length === 0) {
		replace("/");
	}
	callback();
}

export async function updatePlaceAndEvent(
	_prevState: RouterState,
	nextState: RouterState,
	_replace: RedirectFunction,
	callback: () => void | undefined,
) {
	await setPlaceAndEventContext(nextState);
	checkPlaceExpired(nextState);
	checkVendorRestrictionRedirect(nextState);
	callback();
}

function isMasterAdminRole(): boolean {
	const currentEmployee = rootStore.authStore.currentEmployee;
	return currentEmployee?.role?.slug === "masterAdmin";
}

async function getWizardStatus(placeId: string): Promise<PlaceWizardStatus | null> {
	try {
		const wizardStatusModel = rootStore.placeStore.getPlaceWizardStatus;

		// "sucess" é o literal de sucesso do fetchModel do projeto (RequestStatus).
		if (wizardStatusModel.value && wizardStatusModel.status === "sucess") {
			return wizardStatusModel.value;
		}

		wizardStatusModel.reset();
		return await wizardStatusModel.fetch({ placeId });
	} catch (error) {
		return null;
	}
}

async function checkWizardAccess(placeId: string): Promise<boolean> {
	const wizardStatus = await getWizardStatus(placeId);

	if (!wizardStatus) {
		return false;
	}

	const hasStepInProgress = wizardStatus.step !== null && !wizardStatus.isFinished;

	return wizardStatus.shouldShowOnboarding || hasStepInProgress;
}

function getRedirectUrl(placeId?: string): string {
	return placeId ? `/place/${placeId}` : "/";
}

export async function requireWizardAccessAndMasterAdmin(
	nextState: RouterState,
	_replace: RedirectFunction,
	callback: () => void | undefined,
) {
	const { placeId } = nextState.params;

	if (!placeId) {
		callback();
		return;
	}

	const redirectToPlace = () => {
		rootStore.routerStore.push(getRedirectUrl(placeId));
		callback();
	};

	// Master switch: sem a flag WIZARD_LOW_TOUCH, o fluxo do wizard não é acessível.
	const lowTouchEnabled = await isFeatureFlagEnabled(
		FeatureFlagEnum.WIZARD_LOW_TOUCH,
		placeId,
	);
	if (!lowTouchEnabled) {
		redirectToPlace();
		return;
	}

	const hasWizardAccess = await checkWizardAccess(placeId);
	if (!hasWizardAccess) {
		redirectToPlace();
		return;
	}

	const currentEmployee = rootStore.authStore.currentEmployee;
	if (!currentEmployee) {
		rootStore.routerStore.push("/login");
		callback();
		return;
	}

	if (!isMasterAdminRole()) {
		redirectToPlace();
		return;
	}

	callback();
}

type WizardAccessPredicate = (wizardStatus: PlaceWizardStatus | null) => boolean;

async function requireWizardRouteAndMasterAdmin(
	nextState: RouterState,
	_replace: RedirectFunction,
	callback: () => void | undefined,
	allowAccess: WizardAccessPredicate,
) {
	const { placeId } = nextState.params;

	if (!placeId) {
		callback();
		return;
	}

	// Master switch: sem a flag WIZARD_LOW_TOUCH, o fluxo do wizard não é acessível.
	const lowTouchEnabled = await isFeatureFlagEnabled(
		FeatureFlagEnum.WIZARD_LOW_TOUCH,
		placeId,
	);
	if (!lowTouchEnabled) {
		rootStore.routerStore.push(getRedirectUrl(placeId));
		callback();
		return;
	}

	const currentEmployee = rootStore.authStore.currentEmployee;
	if (!currentEmployee) {
		rootStore.routerStore.push("/login");
		callback();
		return;
	}

	if (!isMasterAdminRole()) {
		rootStore.routerStore.push(getRedirectUrl(placeId));
		callback();
		return;
	}

	const wizardStatus = await getWizardStatus(placeId);
	if (!allowAccess(wizardStatus)) {
		rootStore.routerStore.push(getRedirectUrl(placeId));
		callback();
		return;
	}

	callback();
}

export async function requireWizardSummaryAccess(
	nextState: RouterState,
	_replace: RedirectFunction,
	callback: () => void | undefined,
) {
	return requireWizardRouteAndMasterAdmin(
		nextState,
		_replace,
		callback,
		status => !!status && status.step !== null,
	);
}
